// User-sticky feature flags: a change people see (a screen, a price's presentation, wording, a flow) reaches a chosen
// share of them, the same people every time, growing 5% -> 25% -> 50% -> 100%. Vercel has no percentage rollout and a
// NEXT_PUBLIC_ value needs a redeploy; these change without one. In a component, use the hook:
//
//   const retry = useFlag('checkout_status_retry')        // src/hooks/useFlag.js; false until known
//
// When to use one, and how to roll it out and remove it: promptchan-backend docs/workflows/feature-flags.md.
//
// A flag is a percentage, 0 to 100, kept in the backend's PostgreSQL and served by GET /api/flags. A person is in a
// flag when flagBucket(flag, id), a number from 0 to 99, is below its percentage: the first four bytes of SHA-256 of
// "<flag>:<id>", modulo 100, exactly as the backend computes it (myapp/flags.py) for the same person. The id is the uid
// when signed in, and otherwise the visitor id (the pc_vid cookie, which exists only for a visitor who allowed
// analytics); with neither, only a flag at 100 is on. A visitor who signs up moves from their visitor id's group to
// their uid's, so one flag should not span both sides of sign-up. src/lib/__tests__/flags-vector.json pins the hash
// to the backend's (whose tests compare the two files).
//
// It fails safe: a flag never set, at 0 or removed is off, and so is every flag when /api/flags cannot be read.
// No browser dependencies: Web Crypto and fetch only, so it runs in tests, the middleware and the browser alike.
import { parseVisitorCookie } from './visitor-id.js'

export const FLAGS_URL = `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/flags`
const FRESH_MS = 60 * 1000 // the backend answers with max-age=60; a tab open for hours still follows a change
const TIMEOUT_MS = 5000

/** The person's place in the flag, 0 to 99. The same number as the backend's flags.bucket and ops.flag_bucket. */
export async function flagBucket(flag, id) {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(`${flag}:${id}`),
  )
  return new DataView(digest).getUint32(0) % 100
}

/** Whether a percentage lets this person in: 100 is everyone, below it only someone whose bucket is below it. */
export async function decide(flag, id, percent) {
  if (!Number.isInteger(percent) || percent <= 0) return false
  if (percent >= 100) return true
  return Boolean(id) && (await flagBucket(flag, id)) < percent
}

/** The id a person is placed by: the uid, else the visitor id from the pc_vid cookie's value, else null. */
export function flagId(uid, visitorCookie) {
  return uid || parseVisitorCookie(visitorCookie)?.id || null
}

/** {flag: percent} from a body of GET /api/flags, keeping only whole numbers from 0 to 100. */
export function readPercents(body) {
  const found = {}
  const flags = body?.flags
  if (!flags || typeof flags !== 'object' || Array.isArray(flags)) return found
  for (const [flag, percent] of Object.entries(flags)) {
    if (
      /^[a-z][a-z0-9_]{1,59}$/.test(flag) &&
      Number.isInteger(percent) &&
      percent >= 0 &&
      percent <= 100
    )
      found[flag] = percent
  }
  return found
}

let loaded = null // { at, promise }: one request per minute at most, shared by every caller

/** The flags' percentages, {} when they cannot be read. Asked at most once a minute. */
export function loadFlags({
  fetchImpl = fetch,
  now = Date.now(),
  url = FLAGS_URL,
} = {}) {
  if (loaded && now - loaded.at < FRESH_MS) return loaded.promise
  // AbortSignal.timeout is missing before Safari 16: there the request just has no time limit.
  const signal =
    typeof AbortSignal.timeout === 'function'
      ? AbortSignal.timeout(TIMEOUT_MS)
      : undefined
  const promise = fetchImpl(url, { signal })
    .then((response) => (response.ok ? response.json() : null))
    .then(readPercents, () => ({}))
  loaded = { at: now, promise }
  return promise
}

/** Forget the flags read (tests). */
export function forgetFlags() {
  loaded = null
}

/** Whether this person (a uid or visitor id; null for neither) is in the flag. Never rejects. */
export async function isEnabled(flag, id, percents) {
  try {
    const known = percents ?? (await loadFlags())
    return await decide(flag, id, Object.hasOwn(known, flag) ? known[flag] : 0)
  } catch {
    return false
  }
}
